import { useMutation, useQuery } from '@tanstack/react-query'
import {
	Button,
	Checkbox,
	CollapsibleSection,
	Input,
	Modal,
	SpinnerWithBackground,
	Text,
	TextArea,
} from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useToast } from '../../../../utils/ToastContext'
import { trpc } from '../../../../utils/trpc'
import type { AiSummaryConfig } from '../../types'

interface AiSummaryConfigFormData {
	label: string
	globalPrompt?: string
	inputQuestionIds: string[]
	rolesConsidered: string[]
	prompt: string
	triggerWorkflowStep: string
	minCharThreshold: number
	minCommentThreshold: number
	displayAfterComponentId?: string
	viewPermissions: Array<{
		workflowStepId: string
		allowedRoles: string[]
	}>
}

interface AiSummaryConfigModalProps {
	cycleId: string
	config?: AiSummaryConfig
	onClose: () => void
	isOpen: boolean
	isLoading?: boolean
	error?: { message: string } | null
}

export const AiSummaryConfigModal = ({
	cycleId,
	config,
	onClose,
	isOpen,
	isLoading = false,
	error = null,
}: AiSummaryConfigModalProps) => {
	const toast = useToast()

	const {
		control,
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm<AiSummaryConfigFormData>({
		defaultValues: {
			label: '',
			globalPrompt:
				'Format the summary using bullet points. Use a conversational but professional tone. Avoid personal pronouns and any preamble.',
			inputQuestionIds: [],
			rolesConsidered: [],
			prompt: 'Analyze the feedback and identify key themes, strengths, and areas for development.',
			triggerWorkflowStep: '',
			minCharThreshold: 25,
			minCommentThreshold: 3,
			displayAfterComponentId: '',
			viewPermissions: [],
		},
	})

	const formData = watch()

	useEffect(() => {
		if (!isOpen) return

		if (config) {
			reset({
				label: config.label || '',
				globalPrompt: config.globalPrompt || '',
				inputQuestionIds: config.inputQuestionIds || [],
				rolesConsidered: config.rolesConsidered || [],
				prompt: config.prompt || '',
				triggerWorkflowStep: config.triggerWorkflowStep || '',
				minCharThreshold: config.minCharThreshold || 25,
				minCommentThreshold: config.minCommentThreshold || 3,
				displayAfterComponentId: (config as { displayAfterComponentId?: string }).displayAfterComponentId || '',
				viewPermissions: config.viewPermissions || [],
			})
		} else {
			reset({
				label: '',
				globalPrompt:
					'Format the summary using bullet points. Use a conversational but professional tone. Avoid personal pronouns and any preamble.',
				inputQuestionIds: [],
				rolesConsidered: [],
				prompt: 'Analyze the feedback and identify key themes, strengths, and areas for development.',
				triggerWorkflowStep: '',
				minCharThreshold: 25,
				minCommentThreshold: 3,
				displayAfterComponentId: '',
				viewPermissions: [],
			})
		}
	}, [config, reset, isOpen])

	// Get form components (questions) for the cycle
	const { data: formComponents } = useQuery({
		...trpc.admin.forms.getComponentsByCycle.queryOptions({ cycleId }),
		enabled: !!cycleId,
	})

	// Get cycle steps
	const { data: cycleSteps } = useQuery({
		...trpc.admin.cycles.steps.list.queryOptions({ cycleId }),
		enabled: !!cycleId,
	})

	const createMutation = useMutation({
		...trpc.admin.aiSummaries.configs.create.mutationOptions(),
		onSuccess: () => {
			toast.showToast('Configuration created successfully')
			onClose()
		},
		onError: (error) => {
			toast.showToast(`Failed to create configuration: ${error.message}`, 'error')
		},
	})

	const updateMutation = useMutation({
		...trpc.admin.aiSummaries.configs.edit.mutationOptions(),
		onSuccess: () => {
			toast.showToast('Configuration updated successfully')
			onClose()
		},
		onError: (error) => {
			toast.showToast(`Failed to update configuration: ${error.message}`, 'error')
		},
	})

	const uniqueFormComponents = formComponents
		? formComponents.filter((component, index, arr) => arr.findIndex((c) => c.id === component.id) === index)
		: []

	const questionsByFormAndSection = uniqueFormComponents
		.filter((c) => c.type === 'input' || c.label)
		.reduce(
			(acc, component) => {
				const compWithSection = component as typeof component & { sectionName?: string }
				const formName = compWithSection.formName || 'Unnamed Form'
				const sectionName = compWithSection.sectionName || 'General'
				const type = component.type || 'other'

				if (!acc[formName]) {
					acc[formName] = {}
				}
				if (!acc[formName][sectionName]) {
					acc[formName][sectionName] = {}
				}
				if (!acc[formName][sectionName][type]) {
					acc[formName][sectionName][type] = []
				}

				let displayLabel = ''

				const comp = component as unknown as {
					custom?: { label?: string }
					label?: string | { text?: string; value?: string; label?: string }
					name?: string
					order?: number
				}

				if (comp.custom && typeof comp.custom === 'object') {
					const customLabel = comp.custom.label
					if (customLabel && typeof customLabel === 'string') {
						displayLabel = customLabel
					}
				}

				if (!displayLabel && component.label) {
					if (typeof component.label === 'string') {
						displayLabel = component.label
					} else if (typeof component.label === 'object' && component.label !== null) {
						const labelObj = component.label as { text?: string; value?: string; label?: string }
						displayLabel = labelObj.text || labelObj.value || labelObj.label || ''
					}
				}

				if (!displayLabel) {
					displayLabel = component.name || `Question ${component.order || ''}`
				}

				acc[formName][sectionName][type].push({
					value: component.id,
					label: displayLabel,
					order: component.order || 0,
				})

				return acc
			},
			{} as Record<string, Record<string, Record<string, Array<{ value: string; label: string; order: number }>>>>,
		)

	Object.keys(questionsByFormAndSection).forEach((formName) => {
		Object.keys(questionsByFormAndSection[formName]).forEach((sectionName) => {
			Object.keys(questionsByFormAndSection[formName][sectionName]).forEach((type) => {
				questionsByFormAndSection[formName][sectionName][type].sort((a, b) => a.order - b.order)
			})
		})
	})

	const getTypeLabel = (type: string): string => {
		const typeLabels: Record<string, string> = {
			input: 'Text Input',
			textarea: 'Long Text',
			select: 'Selection',
			rating: 'Rating',
			number: 'Number',
			checkbox: 'Checkbox',
		}
		return typeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1)
	}

	const roleOptions = [
		{ value: 'participant', label: 'Participant (Self-evaluation)' },
		{ value: 'primary_manager', label: 'Primary Manager' },
		{ value: 'secondary_manager', label: 'Secondary Manager' },
		{ value: 'rater', label: 'Rater (Peer/Group)' },
		{ value: 'unsolicited', label: 'Unsolicited' },
	]

	const stepOptions =
		cycleSteps?.map((step) => ({
			value: step.id,
			label: step.name,
		})) || []

	const onSubmit = async (data: AiSummaryConfigFormData) => {
		if (!data.label) {
			toast.showToast('Please enter a label', 'error')
			return
		}

		if (data.inputQuestionIds.length === 0) {
			toast.showToast('Please select at least one input question', 'error')
			return
		}

		if (data.rolesConsidered.length === 0) {
			toast.showToast('Please select at least one role', 'error')
			return
		}

		if (!data.prompt) {
			toast.showToast('Please enter a prompt', 'error')
			return
		}

		if (!data.triggerWorkflowStep) {
			toast.showToast('Please select a trigger workflow step', 'error')
			return
		}

		const validPermissions = data.viewPermissions.filter((p) => p.allowedRoles && p.allowedRoles.length > 0)

		if (validPermissions.length === 0) {
			toast.showToast('Please configure view permissions for at least one workflow step', 'error')
			return
		}

		const submissionData = {
			...data,
			globalPrompt: data.globalPrompt || undefined,
			displayAfterComponentId: data.displayAfterComponentId || undefined,
			viewPermissions: validPermissions,
			cycleId,
		}

		try {
			if (config) {
				await updateMutation.mutateAsync({
					id: config.id,
					...submissionData,
				})
			} else {
				await createMutation.mutateAsync(submissionData)
			}
		} catch {}
	}

	const toggleQuestion = (questionId: string) => {
		const currentIds = formData.inputQuestionIds || []
		const newIds = currentIds.includes(questionId)
			? currentIds.filter((id) => id !== questionId)
			: [...currentIds, questionId]
		setValue('inputQuestionIds', newIds)
	}

	const toggleRole = (roleName: string) => {
		const currentRoles = formData.rolesConsidered || []
		const newRoles = currentRoles.includes(roleName)
			? currentRoles.filter((r) => r !== roleName)
			: [...currentRoles, roleName]
		setValue('rolesConsidered', newRoles)
	}

	const toggleViewPermission = (stepId: string, roleName: string) => {
		const existingPermissions = formData.viewPermissions || []
		const stepIndex = existingPermissions.findIndex((p) => p.workflowStepId === stepId)

		let newPermissions: Array<{ workflowStepId: string; allowedRoles: string[] }>

		if (stepIndex === -1) {
			// Create new permission for this step
			newPermissions = [
				...existingPermissions,
				{
					workflowStepId: stepId,
					allowedRoles: [roleName],
				},
			]
		} else {
			// Update existing permission - create new arrays to avoid mutation
			const currentPermission = existingPermissions[stepIndex]
			const currentRoles = currentPermission.allowedRoles || []

			let newRoles: string[]
			if (currentRoles.includes(roleName)) {
				// Remove role
				newRoles = currentRoles.filter((r) => r !== roleName)
			} else {
				// Add role
				newRoles = [...currentRoles, roleName]
			}

			// Create new permissions array with updated permission
			newPermissions = existingPermissions.map((permission, index) =>
				index === stepIndex ? { ...permission, allowedRoles: newRoles } : permission,
			)

			// Remove permissions with no roles
			newPermissions = newPermissions.filter((p) => p.allowedRoles.length > 0)
		}

		setValue('viewPermissions', newPermissions)
	}

	const isRoleSelected = (stepId: string, roleName: string) => {
		const permissions = formData.viewPermissions || []
		const permission = permissions.find((p) => p.workflowStepId === stepId)
		const isSelected = permission?.allowedRoles?.includes(roleName) || false
		return isSelected
	}

	useEffect(() => {
		if (error && isOpen) {
			toast.showToast('Failed to load configuration', 'error')
			onClose()
		}
	}, [error, isOpen, toast, onClose])

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={config ? 'Edit AI Summary Configuration' : 'Create AI Summary Configuration'}
			className='!w-[90vw] !max-w-[1200px] !mx-4'
			bodyClassName='max-h-[88vh] overflow-y-auto'
			closeOnOverlayClick={false}>
			{isLoading ? (
				<div className='flex items-center justify-center py-12'>
					<SpinnerWithBackground />
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
					<div>
						<Text variant='medium' weight='semibold' className='mb-2'>
							Label *
						</Text>
						<Controller
							name='label'
							control={control}
							rules={{ required: 'Label is required' }}
							render={({ field }) => (
								<Input
									{...field}
									placeholder='e.g., Manager Feedback Summary'
									required
									maxLength={255}
									hint={errors.label?.message}
									variant={errors.label ? 'error' : 'default'}
								/>
							)}
						/>
					</div>

					<div>
						<Text variant='medium' weight='semibold' className='mb-2'>
							Global Prompt (Optional)
						</Text>
						<Controller
							name='globalPrompt'
							control={control}
							render={({ field }) => (
								<TextArea
									{...field}
									placeholder='This prompt will be appended to every summary prompt in the cycle. Use this to provide consistent context or instructions across all AI summaries.'
									rows={3}
									maxLength={1000}
								/>
							)}
						/>
						<Text variant='xsmall' className='text-neutral-600 mt-1'>
							This global prompt will be included in every AI summary generation for this cycle.
						</Text>
					</div>

					<div>
						<Text variant='medium' weight='semibold' className='mb-2'>
							Input Questions *
						</Text>
						<div className='max-h-96 overflow-y-auto border border-neutral-300 rounded-md'>
							{Object.keys(questionsByFormAndSection).length === 0 ? (
								<div className='text-neutral-500 text-center py-4'>No questions available</div>
							) : (
								<div className='divide-y divide-neutral-200'>
									{Object.entries(questionsByFormAndSection).map(([formName, questionsBySection]) => (
										<CollapsibleSection key={formName} title={formName} defaultOpen={false}>
											<div className='px-4 pb-3'>
												{Object.entries(questionsBySection).map(([sectionName, questionsByType]) => (
													<div key={sectionName} className='mt-4 first:mt-0'>
														{sectionName !== 'General' && (
															<div className='mb-3 pb-2 border-b border-neutral-200'>
																<Text variant='small' weight='semibold' className='text-neutral-700'>
																	{sectionName}
																</Text>
															</div>
														)}
														<div className='space-y-3'>
															{Object.entries(questionsByType).map(([type, questions]) => (
																<div key={type}>
																	<Text
																		variant='xsmall'
																		weight='semibold'
																		className='text-neutral-500 uppercase tracking-wide mb-2 block'>
																		{getTypeLabel(type)} ({questions.length})
																	</Text>
																	<div className='space-y-2 pl-2'>
																		{questions.map((question) => (
																			<div key={question.value} className='flex items-start'>
																				<Checkbox
																					label={question.label}
																					checked={formData.inputQuestionIds.includes(question.value)}
																					onChange={() => toggleQuestion(question.value)}
																				/>
																			</div>
																		))}
																	</div>
																</div>
															))}
														</div>
													</div>
												))}
											</div>
										</CollapsibleSection>
									))}
								</div>
							)}
						</div>
					</div>

					<div>
						<Text variant='medium' weight='semibold' className='mb-2'>
							Roles Considered *
						</Text>
						<div className='border border-neutral-300 rounded-md p-4'>
							<div className='space-y-2'>
								{roleOptions.map((role) => (
									<Checkbox
										key={role.value}
										label={role.label}
										checked={formData.rolesConsidered.includes(role.value)}
										onChange={() => toggleRole(role.value)}
									/>
								))}
							</div>
						</div>
					</div>

					<div>
						<Text variant='medium' weight='semibold' className='mb-2'>
							Summary Prompt *
						</Text>
						<Controller
							name='prompt'
							control={control}
							rules={{ required: 'Prompt is required' }}
							render={({ field }) => (
								<TextArea
									{...field}
									placeholder='Describe what you want the AI to focus on when generating the summary'
									rows={3}
									maxLength={1000}
									required
									hint={errors.prompt?.message}
									variant={errors.prompt ? 'error' : 'default'}
								/>
							)}
						/>
					</div>

					<div>
						<Text variant='medium' weight='semibold' className='mb-2'>
							Trigger Workflow Step *
						</Text>
						<Text variant='small' className='text-neutral-600 mb-2'>
							Select when the AI summary should be generated.
						</Text>
						<div className='border border-neutral-300 rounded-md p-4'>
							<Controller
								name='triggerWorkflowStep'
								control={control}
								rules={{ required: 'Trigger workflow step is required' }}
								render={({ field }) => (
									<div className='space-y-2'>
										{stepOptions.map((step) => (
											<Checkbox
												key={step.value}
												label={step.label}
												checked={field.value === step.value}
												onChange={() => field.onChange(field.value === step.value ? '' : step.value)}
											/>
										))}
									</div>
								)}
							/>
						</div>
						{errors.triggerWorkflowStep && (
							<Text variant='xsmall' className='text-red-600 mt-1'>
								{errors.triggerWorkflowStep.message}
							</Text>
						)}
					</div>

					<div>
						<Text variant='medium' weight='semibold' className='mb-2'>
							View Permissions *
						</Text>
						<Text variant='small' className='text-neutral-600 mb-2'>
							Configure who can view the AI summary at each workflow step.
						</Text>
						<div className='space-y-3 max-h-96 overflow-y-auto border border-neutral-300 rounded-md p-4'>
							{stepOptions.map((step) => {
								const selectedRoles =
									formData.viewPermissions.find((p) => p.workflowStepId === step.value)?.allowedRoles || []

								return (
									<div key={step.value} className='border border-neutral-200 rounded-md p-4'>
										<div className='flex items-center justify-between mb-3'>
											<Text variant='small' weight='semibold'>
												{step.label}
											</Text>
											<Text variant='xsmall' className='text-neutral-500'>
												{selectedRoles.length} selected
											</Text>
										</div>
										<div className='space-y-2'>
											{roleOptions.map((role) => {
												const isChecked = isRoleSelected(step.value, role.value)
												return (
													<Checkbox
														key={`${step.value}-${role.value}`}
														label={role.label}
														checked={isChecked}
														onChange={() => toggleViewPermission(step.value, role.value)}
													/>
												)
											})}
										</div>
									</div>
								)
							})}
						</div>
					</div>

					{/* Display Position */}
					<div>
						<Text variant='medium' weight='semibold' className='mb-2'>
							Display Position
						</Text>
						<Text variant='small' className='text-neutral-600 mb-2'>
							Select where the AI summary should appear in the form. Leave blank to display at the bottom.
						</Text>
						<div className='max-h-96 overflow-y-auto border border-neutral-300 rounded-md'>
							{Object.keys(questionsByFormAndSection).length === 0 ? (
								<div className='text-neutral-500 text-center py-4'>No questions available</div>
							) : (
								<div className='divide-y divide-neutral-200'>
									{Object.entries(questionsByFormAndSection).map(([formName, questionsBySection]) => (
										<CollapsibleSection key={formName} title={formName} defaultOpen={false}>
											<div className='px-4 pb-3'>
												{Object.entries(questionsBySection).map(([sectionName, questionsByType]) => (
													<div key={sectionName} className='mt-4 first:mt-0'>
														{sectionName !== 'General' && (
															<div className='mb-3 pb-2 border-b border-neutral-200'>
																<Text variant='small' weight='semibold' className='text-neutral-700'>
																	{sectionName}
																</Text>
															</div>
														)}
														<div className='space-y-2'>
															{Object.entries(questionsByType).map(([, questions]) =>
																questions.map((question) => (
																	<button
																		type='button'
																		key={question.value}
																		className={`flex items-center p-2 rounded hover:bg-neutral-50 cursor-pointer transition-colors ${
																			formData.displayAfterComponentId === question.value
																				? 'bg-primary-50 border border-primary-300'
																				: 'border border-transparent'
																		}`}
																		onClick={() => {
																			setValue(
																				'displayAfterComponentId',
																				formData.displayAfterComponentId === question.value ? '' : question.value,
																			)
																		}}>
																		<div
																			className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
																				formData.displayAfterComponentId === question.value
																					? 'border-primary-600 bg-primary-600'
																					: 'border-neutral-300'
																			}`}>
																			{formData.displayAfterComponentId === question.value && (
																				<div className='w-2 h-2 bg-white rounded-full' />
																			)}
																		</div>
																		<Text variant='small'>{question.label}</Text>
																	</button>
																)),
															)}
														</div>
													</div>
												))}
											</div>
										</CollapsibleSection>
									))}
								</div>
							)}
						</div>
						{formData.displayAfterComponentId && (
							<div className='mt-2 flex items-center gap-2'>
								<Text variant='xsmall' className='text-primary-600'>
									AI summary will appear after the selected question
								</Text>
								<button
									type='button'
									onClick={() => setValue('displayAfterComponentId', '')}
									className='text-xsmall text-neutral-500 hover:text-neutral-700 underline'>
									Clear selection
								</button>
							</div>
						)}
					</div>

					{/* Advanced Options */}
					<CollapsibleSection title='Advanced Options' defaultOpen={false}>
						<div className='space-y-4 mt-4'>
							<div>
								<Text variant='small' weight='regular'>
									Minimum Character Threshold
								</Text>
								<Controller
									name='minCharThreshold'
									control={control}
									render={({ field }) => (
										<Input
											{...field}
											value={field.value?.toString() || ''}
											type='number'
											placeholder='25'
											min={1}
											max={1000}
											onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 25)}
											onBlur={(e) => {
												const val = Number.parseInt(e.target.value, 10)
												field.onChange(Number.isNaN(val) || val < 1 ? 25 : val)
											}}
										/>
									)}
								/>
								<Text variant='xsmall' className='mt-1 text-neutral-600'>
									Comments with fewer characters than this will be excluded from the summary.
								</Text>
							</div>

							<div>
								<Text variant='small' weight='regular'>
									Minimum Comment Threshold
								</Text>
								<Controller
									name='minCommentThreshold'
									control={control}
									render={({ field }) => (
										<Input
											{...field}
											value={field.value?.toString() || ''}
											type='number'
											placeholder='3'
											min={1}
											max={100}
											onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 3)}
											onBlur={(e) => {
												const val = Number.parseInt(e.target.value, 10)
												field.onChange(Number.isNaN(val) || val < 1 ? 3 : val)
											}}
										/>
									)}
								/>
								<Text variant='xsmall' className='mt-1 text-neutral-600'>
									Minimum number of comments required to generate a summary.
								</Text>
							</div>
						</div>
					</CollapsibleSection>

					{/* Submit Button */}
					<div className='flex justify-end gap-3'>
						<Button variant='secondary' onClick={onClose}>
							Cancel
						</Button>
						<Button type='submit' disabled={createMutation.isPending || updateMutation.isPending}>
							{createMutation.isPending || updateMutation.isPending
								? config
									? 'Updating...'
									: 'Creating...'
								: config
									? 'Update Configuration'
									: 'Create Configuration'}
						</Button>
					</div>
				</form>
			)}
		</Modal>
	)
}
